import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState([]);
  const [recentWebsites, setRecentWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Inline styles as fallback
  const styles = {
    dashboardContainer: {
      background: '#f8f9fa',
      minHeight: '100vh',
      padding: '2rem'
    },
    dashboardHeader: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      borderRadius: '20px',
      padding: '2rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.9)',
      position: 'relative',
      overflow: 'hidden'
    },
    headerGradient: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(135deg, #0b1083, #9d0003, #ff2f00)'
    },
    dashboardTitle: {
      fontSize: '2.5rem',
      fontWeight: 800,
      background: 'linear-gradient(135deg, #0b1083, #9d0003)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '0.5rem'
    },
    addWebsiteBtn: {
      background: 'linear-gradient(135deg, #0b1083, #9d0003)',
      border: 'none',
      borderRadius: '15px',
      padding: '1rem 2rem',
      fontWeight: 600,
      fontSize: '1.1rem',
      boxShadow: '0 8px 25px rgba(11, 16, 131, 0.3)',
      transition: 'all 0.3s ease'
    },
    statsCard: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      borderRadius: '20px',
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease'
    },
    statsValue: {
      fontSize: '2.5rem',
      fontWeight: 800,
      marginBottom: '0.5rem'
    },
    tableCard: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      borderRadius: '20px',
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        const websitesResponse = await api.get('sites');
        const websitesData = websitesResponse.data.data || [];
        
        const totalWebsites = websitesData.length;
        const activeWebsites = websitesData.filter(site => site.status === 'active').length;
        
        const calculatedStats = [
          {
            title: 'Total Websites',
            value: totalWebsites.toString(),
            change: '+2',
            changeType: 'positive',
            icon: 'fa-globe',
            color: 'primary'
          },
          {
            title: 'Active Websites',
            value: activeWebsites.toString(),
            change: '+1',
            changeType: 'positive',
            icon: 'fa-check-circle',
            color: 'success'
          },
          {
            title: 'Storage Used',
            value: '2.4GB',
            change: '+0.3GB',
            changeType: 'positive',
            icon: 'fa-database',
            color: 'info'
          },
          {
            title: 'System Health',
            value: '98%',
            change: '+2%',
            changeType: 'positive',
            icon: 'fa-heart',
            color: 'danger'
          }
        ];

        const formattedWebsites = websitesData.slice(0, 5).map(site => ({
          id: site.id,
          name: site.brand_name,
          domain: site.http_link,
          status: site.status || 'active',
          users: Math.floor(Math.random() * 5000) + 500,
          created: site.created_at ? new Date(site.created_at).toLocaleDateString() : 'N/A'
        }));

        setStats(calculatedStats);
        setRecentWebsites(formattedWebsites);
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
        setStats(getMockStats());
        setRecentWebsites(getMockWebsites());
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getMockStats = () => [
    {
      title: 'Total Websites',
      value: '24',
      change: '+3',
      changeType: 'positive',
      icon: 'fa-globe',
      color: 'primary'
    },
    {
      title: 'Active Admins',
      value: '18',
      change: '+2',
      changeType: 'positive',
      icon: 'fa-users',
      color: 'success'
    },
    {
      title: 'Storage Used',
      value: '2.4GB',
      change: '+0.3GB',
      changeType: 'positive',
      icon: 'fa-database',
      color: 'info'
    },
    {
      title: 'System Health',
      value: '98%',
      change: '+2%',
      changeType: 'positive',
      icon: 'fa-heart',
      color: 'danger'
    }
  ];

  const getMockWebsites = () => [
    { id: 1, name: 'Tech Blog', domain: 'techblog.com', status: 'active', users: 1243, created: '2024-01-15' },
    { id: 2, name: 'E-commerce Store', domain: 'store.example.com', status: 'active', users: 2890, created: '2024-01-14' },
    { id: 3, name: 'Portfolio Site', domain: 'portfolio.dev', status: 'inactive', users: 567, created: '2024-01-13' },
    { id: 4, name: 'News Portal', domain: 'newsportal.org', status: 'active', users: 4156, created: '2024-01-12' },
  ];

  const getStatusVariant = (status) => {
    return status === 'active' ? 'success' : 'secondary';
  };

  if (loading) {
    return (
      <div style={styles.dashboardContainer}>
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.dashboardContaineruuuu}>
      {/* Header */}
      <div style={styles.dashboardHeader}>
        <div style={styles.headerGradient}></div>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 style={styles.dashboardTitle}>Super Admin Dashboard</h1>
            <p className="text-muted">Overview of all websites and system performance</p>
          </div>
          <Button 
            as={Link} 
            to="/super-admin/websites/create" 
            style={styles.addWebsiteBtn}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 12px 35px rgba(11, 16, 131, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 25px rgba(11, 16, 131, 0.3)';
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Website
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Stats Grid */}
      <Row className="g-4 mb-4">
        {stats.map((stat, index) => (
          <Col key={index} xs={12} sm={6} lg={3}>
            <Card style={styles.statsCard} 
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)';
                  }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-2 fw-semibold">{stat.title}</p>
                    <h3 style={styles.statsValue}>{stat.value}</h3>
                    <div className="d-flex align-items-center">
                      <span className={`text-${stat.changeType === 'positive' ? 'success' : 'danger'} me-1 fw-semibold`}>
                        {stat.change}
                      </span>
                      <small className="text-muted">vs last month</small>
                    </div>
                  </div>
                  <div className={`bg-${stat.color} text-white rounded p-3`}
                       style={{width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <i className={`fas ${stat.icon} fs-5`}></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Content Grid */}
      <Row className="g-4">
        <Col lg={8}>
          <Card style={styles.tableCard}>
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-semibold">Recent Websites</h5>
                <Button as={Link} to="/super-admin/websites" variant="outline-primary" size="sm">
                  View All
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {recentWebsites.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-globe fs-1 text-muted mb-3"></i>
                  <h5 className="text-muted">No Websites Found</h5>
                  <p className="text-muted">Get started by creating your first website</p>
                  <Button as={Link} to="/super-admin/websites/create" variant="primary">
                    Create First Website
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead className="bg-light">
                    <tr>
                      <th>Website Name</th>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Users</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentWebsites.map((website) => (
                      <tr key={website.id}>
                        <td className="fw-semibold">{website.name}</td>
                        <td className="text-muted font-monospace">{website.domain}</td>
                        <td>
                          <Badge bg={getStatusVariant(website.status)}>
                            {website.status}
                          </Badge>
                        </td>
                        <td>{website.users.toLocaleString()}</td>
                        <td className="text-muted">{website.created}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card style={styles.tableCard}>
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0 fw-semibold">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button as={Link} to="/super-admin/websites" variant="outline-primary" className="text-start py-3">
                  <i className="fas fa-globe me-2"></i>
                  Manage Websites
                </Button>
                <Button as={Link} to="/super-admin/admins" variant="outline-success" className="text-start py-3">
                  <i className="fas fa-users-cog me-2"></i>
                  Manage Admins
                </Button>
                <Button as={Link} to="/super-admin/analytics" variant="outline-info" className="text-start py-3">
                  <i className="fas fa-chart-line me-2"></i>
                  View Analytics
                </Button>
                <Button as={Link} to="/super-admin/settings" variant="outline-warning" className="text-start py-3">
                  <i className="fas fa-cogs me-2"></i>
                  System Settings
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;